import { useEffect, useState } from 'react';
import { Box, Card, CardContent, Chip, LinearProgress, Typography } from '@mui/material';
import apiService from '../services/api';

/** The laptop itself: GPU memory and heat, who holds the GPU, Ollama's models, the news backlog. */
export default function MachineCard() {
  const [m, setM] = useState<any>(null);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try { const r = await apiService.getMachineHealth(); if (alive) setM(r); } catch { /* shown as absent */ }
    };
    tick();
    const id = setInterval(tick, 15000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (!m) return null;
  const g = m.gpu;
  const hotColor = (t: number, hot: number) => (t >= hot ? '#EF4444' : t >= hot - 10 ? '#F59E0B' : '#22C55E');

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Machine</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
          {g && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                GPU memory: {g.used.toLocaleString()} of {g.total.toLocaleString()} MiB
                ({g.headroom_kept_mib.toLocaleString()} always left for the screen)
              </Typography>
              <LinearProgress variant="determinate" value={Math.min(100, (100 * g.used) / g.total)}
                sx={{ height: 8, borderRadius: 1, mt: 0.5 }} />
              <Typography variant="caption" color="text.disabled">
                Busy {g.util_pct}% · needs: LLM {m.needs_mib.llm} · Kronos {m.needs_mib.kronos} · Chronos {m.needs_mib.chronos} MiB
              </Typography>
            </Box>
          )}
          <Box>
            <Typography variant="caption" color="text.secondary">Heat</Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
              {g && <Chip size="small" label={`GPU ${g.temp_c} °C`}
                sx={{ bgcolor: hotColor(g.temp_c, g.hot_c) + '22', color: hotColor(g.temp_c, g.hot_c), fontWeight: 700 }} />}
              <Chip size="small" label={`CPU ${Math.round(m.cpu_temp_c)} °C`}
                sx={{ bgcolor: hotColor(m.cpu_temp_c, m.cpu_hot_c) + '22', color: hotColor(m.cpu_temp_c, m.cpu_hot_c), fontWeight: 700 }} />
              <Chip size="small" label={`load ${m.load_1m} / ${m.cores} threads`} />
            </Box>
            <Typography variant="caption" color="text.disabled">
              GPU jobs wait at {g?.hot_c ?? 85} °C (GPU) or {m.cpu_hot_c} °C (CPU) until both cool
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">GPU now</Typography>
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
              {m.lease_holder || 'free'}
            </Typography>
            {m.ollama.map((o: any) => (
              <Typography key={o.name} variant="caption" display="block" color="text.disabled">
                Ollama holds {o.name}: {o.vram_mib.toLocaleString()} MiB on the GPU
              </Typography>
            ))}
            <Typography variant="caption" display="block" color="text.disabled">
              News articles waiting for the LLM: {(m.news_backlog ?? 0).toLocaleString()}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
